import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RegionProvider } from "@/context/RegionContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import DemoAccess from "@/pages/DemoAccess";
import SuperAdmin from "@/pages/SuperAdmin";
import AdminOrders from "@/pages/AdminOrders";
import AdminOrderNew from "@/pages/AdminOrderNew";
import AdminDrivers from "@/pages/AdminDrivers";
import AdminReports from "@/pages/AdminReports";
import AdminPharmacies from "@/pages/AdminPharmacies";
import AdminPatients from "@/pages/AdminPatients";
import AdminUsers from "@/pages/AdminUsers";
import AdminVehicles from "@/pages/AdminVehicles";
import AdminCompany from "@/pages/AdminCompany";
import AdminInvoices from "@/pages/AdminInvoices";
import AdminAccounting from "@/pages/AdminAccounting";
import CopaySummary from "@/pages/CopaySummary";
import AdminAuditLog from "@/pages/AdminAuditLog";
import HIPAA from "@/pages/HIPAA";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminTimeSheet from "@/pages/AdminTimeSheet";
import AdminStartLocations from "@/pages/AdminStartLocations";
import Dispatcher from "@/pages/Dispatcher";
import DispatcherOrders from "@/pages/DispatcherOrders";
import PharmacyStaff from "@/pages/PharmacyStaff";
import PharmacyStaffOverview from "@/pages/PharmacyStaffOverview";
import PharmacyStaffInvoices from "@/pages/PharmacyStaffInvoices";
import PharmacyStaffNew from "@/pages/PharmacyStaffNew";
import PharmacyStaffPatients from "@/pages/PharmacyStaffPatients";
import Driver from "@/pages/Driver";
import DriverHome from "@/pages/DriverHome";
import DriverRoutesList from "@/pages/DriverRoutesList";
import DriverTimesheet from "@/pages/DriverTimesheet";
import ScanPackage from "@/pages/ScanPackage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import SmsConsent from "@/pages/SmsConsent";
import HipaaSign from "@/pages/HipaaSign";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import { Toaster } from "@/components/ui/sonner";

function HomeRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) {
    return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!user) return <Navigate to={`/login${location.search}`} replace />;
  const map = {
    super_admin: "/admin",
    dispatcher: "/dispatch",
    pharmacy_staff: "/staff",
    driver: "/driver",
  };
  return <Navigate to={map[user.role] || "/login"} replace />;
}

const wrap = (el, roles) => (
  <ProtectedRoute roles={roles}>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RegionProvider>
          <ThemeProvider>
            <Toaster richColors position="top-right" />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/demo-access" element={<DemoAccess />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/sms-consent" element={<SmsConsent />} />
              <Route path="/hipaa-sign/:token" element={<HipaaSign />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/" element={<HomeRedirect />} />

              <Route path="/admin" element={wrap(<SuperAdmin />, ["super_admin"])} />
              <Route path="/admin/orders" element={wrap(<AdminOrders />, ["super_admin"])} />
              <Route path="/admin/orders/new" element={wrap(<AdminOrderNew />, ["super_admin"])} />
              <Route path="/admin/routes" element={wrap(<Dispatcher />, ["super_admin"])} />
              <Route path="/admin/pharmacies" element={wrap(<AdminPharmacies />, ["super_admin"])} />
              <Route path="/admin/patients" element={wrap(<AdminPatients />, ["super_admin"])} />
              <Route path="/admin/drivers" element={wrap(<AdminDrivers />, ["super_admin"])} />
              <Route path="/admin/users" element={wrap(<AdminUsers />, ["super_admin"])} />
              <Route path="/admin/reports" element={wrap(<AdminReports />, ["super_admin"])} />
              <Route path="/admin/vehicles" element={wrap(<AdminVehicles />, ["super_admin"])} />
              <Route path="/admin/company" element={wrap(<AdminCompany />, ["super_admin"])} />
              <Route path="/admin/invoices" element={wrap(<AdminInvoices />, ["super_admin"])} />
              <Route path="/admin/accounting" element={wrap(<AdminAccounting />, ["super_admin"])} />
              {/* Copay Summary — visible to admin, dispatcher, and pharmacy staff.
                  Pharmacy staff sees only their own pharmacy (scoped server-side). */}
              <Route path="/admin/copay-summary" element={wrap(<CopaySummary />, ["super_admin"])} />
              <Route path="/dispatch/copay-summary" element={wrap(<CopaySummary />, ["super_admin", "dispatcher"])} />
              <Route path="/staff/copay-summary" element={wrap(<CopaySummary />, ["super_admin", "dispatcher", "pharmacy_staff"])} />
              <Route path="/admin/audit-log" element={wrap(<AdminAuditLog />, ["super_admin"])} />
              <Route path="/admin/hipaa" element={wrap(<HIPAA />, ["super_admin"])} />
              <Route path="/admin/timesheet" element={wrap(<AdminTimeSheet />, ["super_admin"])} />
              <Route path="/admin/start-locations" element={wrap(<AdminStartLocations />, ["super_admin", "dispatcher"])} />

              <Route path="/dispatch" element={wrap(<SuperAdmin />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/orders" element={wrap(<AdminOrders />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/orders/new" element={wrap(<AdminOrderNew />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/routes" element={wrap(<Dispatcher />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/audit-log" element={wrap(<AdminAuditLog />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/hipaa" element={wrap(<HIPAA />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/timesheet" element={wrap(<AdminTimeSheet />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/drivers" element={wrap(<AdminDrivers />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/vehicles" element={wrap(<AdminVehicles />, ["super_admin", "dispatcher"])} />
              <Route path="/dispatch/start-locations" element={wrap(<AdminStartLocations />, ["super_admin", "dispatcher"])} />

              <Route path="/staff" element={wrap(<PharmacyStaffOverview />, ["pharmacy_staff", "super_admin"])} />
              <Route path="/staff/orders" element={wrap(<PharmacyStaff />, ["pharmacy_staff", "super_admin"])} />
              <Route path="/staff/new" element={wrap(<PharmacyStaffNew />, ["pharmacy_staff", "super_admin"])} />
              <Route path="/staff/patients" element={wrap(<PharmacyStaffPatients />, ["pharmacy_staff", "super_admin"])} />
              <Route path="/staff/invoices" element={wrap(<PharmacyStaffInvoices />, ["pharmacy_staff"])} />
              <Route path="/staff/hipaa" element={wrap(<HIPAA />, ["pharmacy_staff", "super_admin"])} />

              <Route path="/driver" element={wrap(<DriverHome />, ["driver", "super_admin", "dispatcher"])} />
              <Route path="/driver/routes" element={wrap(<DriverRoutesList />, ["driver", "super_admin", "dispatcher"])} />
              <Route path="/driver/route" element={wrap(<Driver />, ["driver", "super_admin", "dispatcher"])} />
              <Route path="/driver/route/:routeId" element={wrap(<Driver />, ["driver", "super_admin", "dispatcher"])} />
              <Route path="/driver/timesheet" element={wrap(<DriverTimesheet />, ["driver", "super_admin", "dispatcher"])} />
              <Route path="/scan" element={wrap(<ScanPackage />, ["driver", "super_admin", "dispatcher", "pharmacy_staff"])} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ThemeProvider>
          </RegionProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
